<script lang="ts" setup>
import { computed, ref } from 'vue'
import MarkdownIt from 'markdown-it'
import mdKatex from '@traptitech/markdown-it-katex'
import mila from 'markdown-it-link-attributes'
import hljs from 'highlight.js'
import { useBasicLayout } from '@/hooks/useBasicLayout'
// import { t } from '@/locales'

interface Props {
  inversion?: boolean
  error?: string
  text?: string
  loading?: boolean
  asRawText?: boolean
}

const props = defineProps<Props>()

const { isMobile } = useBasicLayout()

const textRef = ref<HTMLElement>()

const mdi = new MarkdownIt({
  linkify: true,
  highlight(code, language) {
    const validLang = !!(language && hljs.getLanguage(language))
    if (validLang) {
      const lang = language ?? ''
      return highlightBlock(hljs.highlight(code, { language: lang }).value, lang)
    }
    return highlightBlock(hljs.highlightAuto(code).value, '')
  },
})

mdi.use(mila, { attrs: { target: '_blank', rel: 'noopener' } })
mdi.use(mdKatex, { blockClass: 'katexmath-block rounded-md p-[10px]', errorColor: ' #cc0000' })

const wrapClass = computed(() => {
  return [
    'text-wrap',
    'min-w-[20px]',
    'rounded-md',
    isMobile.value ? 'p-2' : 'px-3 py-2',
    props.inversion ? 'bg-[#d2f9d1]' : 'bg-[#f4f6f8]',
    props.inversion ? 'dark:bg-[#a1dc95]' : 'dark:bg-[#1e1e20]',
    props.inversion ? 'message-request' : 'message-reply',
    // { 'text-red-500': props.error },
  ]
})

const text = computed(() => {
  const value = props.text ?? ''
  if (!props.asRawText)
    return mdi.render(value)
  return value
})

function highlightBlock(str: string, lang?: string) {
  return `<pre class="code-block-wrapper"><div class="code-block-header"><span class="code-block-header__lang">${lang}</span><span class="code-block-header__copy">拷贝</span></div><code class="hljs code-block-body ${lang}">${str}</code></pre>`
}

defineExpose({ textRef })
</script>

<template>
  <div v-if="!inversion" class="triangle left"></div>
  <div v-else class="triangle right"></div>
  <div class="text-black" :class="wrapClass" >
    <div ref="textRef" class="leading-relaxed break-words">
      <div v-if="!inversion" >
        <div v-if="!asRawText" class="markdown-body" v-html="text"></div>
        <div v-else class="whitespace-pre-wrap" v-text="text"></div>
      </div>
      <div v-else class="whitespace-pre-wrap" v-text="text"></div>
    </div>
    <span v-if="!text" >...</span>
    <!-- <span v-if="!text"  style="color:crimson">|中止逐字接收|</span> -->
    <span v-if="loading && !inversion" class="dark:text-white w-[4px] h-[20px] inline-block animate-blink" ></span>
  </div>
</template>

<style lang="less">
@import url(./style.less);
.message-reply {
  background-color: azure !important;
}

.triangle {
  width: 0;
  height: 0;
  border-bottom: 8px solid transparent;
  border-top: 8px solid transparent;
  margin-top: 8px;
  margin-bottom: auto;
}

.triangle.left {
  border-left: 0;
  border-right: 8px solid azure;
}

.triangle.right {
  border-left: 8px solid rgb(210 249 209);
  border-right: 0;
}

.error {
  color: red;
  background-color: yellow;
}


</style>
